import { useEffect, useMemo, useRef, useState } from 'react';
import useWorldStore from '../store/worldStore';

const mono = { fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif" };
const ACTIVITY_VISIBLE_LIMIT = 250;

function timeAgo(ts, now) {
  const s = Math.floor((now - ts) / 1000);
  if (s < 5) return 'now';
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  return `${Math.floor(s / 3600)}h`;
}

const TYPE_STYLES = {
  task_queue:    { prefix: '⋯', color: 'rgba(255,255,255,0.55)' },
  task_start:    { prefix: '⚡', color: 'rgba(255,255,255,0.85)' },
  task_complete: { prefix: '✓', color: '#4ade80' },
  task_fail:     { prefix: '✗', color: '#ef4444' },
  task_step:     { prefix: '', color: 'rgba(255,255,255,0.3)', indent: true },
  delegation:    { prefix: '→', color: '#fbbf24' },
  life_sim:      { prefix: '', color: 'rgba(255,255,255,0.45)' },
  zone_change:   { prefix: '', color: 'rgba(255,255,255,0.35)' },
  chat:          { prefix: '💬', color: 'rgba(255,255,255,0.5)' },
};

function ActivityEntry({ event, agentById, now }) {
  const agent = agentById.get(event.agentId);
  const color = agent?.color || '#888';
  const style = TYPE_STYLES[event.type] || TYPE_STYLES.life_sim;

  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 6,
      padding: '3px 0',
      paddingLeft: style.indent ? 16 : 0,
      animation: 'entrySlideIn 0.2s ease-out',
    }}>
      <div style={{ width: 6, height: 6, borderRadius: '50%', background: color, marginTop: 4, flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <span style={{ ...mono, fontSize: 9, color, fontWeight: 600 }}>{agent?.name || '?'}</span>
        <span style={{ ...mono, fontSize: 9, color: 'rgba(255,255,255,0.2)', margin: '0 4px' }}>·</span>
        <span style={{ ...mono, fontSize: 9, color: style.color }}>
          {style.prefix ? `${style.prefix} ` : ''}{event.message}
        </span>
        {event.emoji && event.type !== 'task_start' && event.type !== 'task_complete' && event.type !== 'task_fail' && (
          <span style={{ marginLeft: 3, fontSize: 9 }}>{event.emoji}</span>
        )}
      </div>
      <span style={{ ...mono, fontSize: 8, color: 'rgba(255,255,255,0.15)', flexShrink: 0 }}>
        {timeAgo(event.timestamp, now)}
      </span>
    </div>
  );
}

export default function ActivityFeed() {
  const visible = useWorldStore((s) => s.activityFeedVisible);
  if (!visible) return null;
  return <ActivityFeedBody />;
}

function ActivityFeedBody() {
  const activityLog = useWorldStore((s) => s.activityLog);
  const agents = useWorldStore((s) => s.agents);
  const [filter, setFilter] = useState('all');
  const [autoScroll, setAutoScroll] = useState(true);
  const [now, setNow] = useState(() => Date.now());
  const scrollRef = useRef();
  const prevLength = useRef(0);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 10000);
    return () => clearInterval(t);
  }, []);

  const agentById = useMemo(() => new Map(agents.map((agent) => [agent.id, agent])), [agents]);

  const filtered = useMemo(() => {
    const seenIds = new Set();
    const rows = [];
    for (const event of activityLog) {
      if (filter !== 'all' && event.agentId !== filter) continue;
      if (!event?.id || seenIds.has(event.id)) continue;
      seenIds.add(event.id);
      rows.push(event);
    }
    return rows
      .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0))
      .slice(0, ACTIVITY_VISIBLE_LIMIT);
  }, [activityLog, filter]);

  useEffect(() => {
    if (autoScroll && scrollRef.current && activityLog.length > prevLength.current) {
      scrollRef.current.scrollTop = 0;
    }
    prevLength.current = activityLog.length;
  }, [activityLog.length, autoScroll]);

  const handleScroll = () => {
    if (!scrollRef.current) return;
    const { scrollTop } = scrollRef.current;
    setAutoScroll(scrollTop < 40);
  };

  return (
    <div style={{
      position: 'fixed', top: 40, left: 0,
      width: 300, height: 'calc(100vh - 40px)',
      background: 'var(--glass-bg)',
      backdropFilter: 'blur(var(--glass-blur))',
      borderRight: '1px solid var(--color-border)',
      zIndex: 50, display: 'flex', flexDirection: 'column',
      animation: 'feedIn 0.2s ease-out',
    }}
      data-testid="ego-world-activity-feed">
      <style>{`
        @keyframes feedIn { from { transform: translateX(-100%); } to { transform: translateX(0); } }
        @keyframes entrySlideIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>

      {/* Header */}
      <div style={{ padding: '42px 12px 6px', flexShrink: 0, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
          <div style={{
            width: 6, height: 6, borderRadius: '50%', background: '#4ade80',
            animation: 'indicatorPulse 2s ease-in-out infinite',
          }} />
          <span style={{ ...mono, fontSize: 10, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
            Activity
          </span>
        </div>

        {/* Filter pills */}
        <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          <button onClick={() => setFilter('all')} style={pillStyle(filter === 'all', 'rgba(255,255,255,0.5)')}>All</button>
          {agents.map((a) => (
            <button key={a.id} onClick={() => setFilter(filter === a.id ? 'all' : a.id)}
              style={pillStyle(filter === a.id, a.color)}>
              {a.name}
            </button>
          ))}
        </div>
      </div>

      {/* Feed */}
      <div ref={scrollRef} onScroll={handleScroll}
        style={{ flex: 1, overflowY: 'auto', padding: '6px 10px' }}>
        {filtered.length === 0 ? (
          <div style={{ ...mono, fontSize: 9, color: 'rgba(255,255,255,0.2)', textAlign: 'center', padding: 20 }}>
            No activity yet
          </div>
        ) : (
          filtered.map((event) => (
            <ActivityEntry key={event.id} event={event} agentById={agentById} now={now} />
          ))
        )}
      </div>

      {/* New activity pill */}
      {!autoScroll && (
        <button onClick={() => {
          setAutoScroll(true);
          if (scrollRef.current) scrollRef.current.scrollTop = 0;
        }} style={{
          position: 'absolute', bottom: 8, left: '50%', transform: 'translateX(-50%)',
          ...mono, fontSize: 9, color: '#4ade80',
          background: 'rgba(8,10,16,0.95)', border: '1px solid rgba(74,222,128,0.3)',
          borderRadius: 12, padding: '3px 10px', cursor: 'pointer',
        }}>
          ↑ New activity
        </button>
      )}

      <style>{`
        @keyframes indicatorPulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.2); }
        }
      `}</style>
    </div>
  );
}

function pillStyle(active, color) {
  return {
    ...{ fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif" },
    fontSize: 8, background: active ? `${color}22` : 'transparent',
    border: `1px solid ${active ? `${color}55` : 'rgba(255,255,255,0.08)'}`,
    color: active ? color : 'rgba(255,255,255,0.3)',
    borderRadius: 10, padding: '1px 7px', cursor: 'pointer',
  };
}
